/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/
import {Observable} from 'rxjs';
import {Injectable} from '@angular/core';
import {SpinnerContextTypeEnum} from './spinner-context-type.enum';
import {SpinnerGlobalContext} from './spinner-global-context';
import {SpinnerLocalContext} from './spinner-local-context';

@Injectable({
    providedIn: 'root'
})
export class SpinnerService {
    private readonly globalContext = new SpinnerGlobalContext();
    private readonly localContext = new SpinnerLocalContext();

    /**
     * Starts a loading spinner.
     * @param context The context of the loading spinner.
     * @param victim The reference for the loading spinner.
     */
    start<T>(context: SpinnerContextTypeEnum, victim: T): void {
        switch (context) {
            case SpinnerContextTypeEnum.GLOBAL:
                this.globalContext.add(victim);
                break;
            case SpinnerContextTypeEnum.LOCAL:
                this.localContext.add(victim);
                break;
        }
    }

    /**
     * Stops a loading spinner.
     * @param context The context of the loading spinner.
     * @param victim The reference for the loading spinner.
     */
    stop<T>(context: SpinnerContextTypeEnum, victim: T): void {
        switch (context) {
            case SpinnerContextTypeEnum.GLOBAL:
                this.globalContext.remove(victim);
                break;
            case SpinnerContextTypeEnum.LOCAL:
                this.localContext.remove(victim);
                break;
        }
    }

    /**
     * Gets the observable responsible for the latest change to the global loading spinner.
     */
    onGlobalChange(): Observable<boolean> {
        return this.globalContext.onChange();
    }

    /**
     * Returns the observable responsible for the latest change to the local loading spinner.
     * @param victim The reference for the loading spinner.
     */
    onLocalChange<T>(victim: T): Observable<boolean> {
        return this.localContext.onChange(victim);
    }
}
